import React, { useState, useRef, useEffect } from 'react';
import { Send, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { MessageBubble } from './MessageBubble';
import { AssignmentMessageCard, AssignmentMessageMetadata } from './AssignmentMessageCard';
import { AssignmentResponseCard, AssignmentResponseMetadata } from './AssignmentResponseCard';
import { VisitScheduledCard, VisitScheduledMetadata } from './VisitScheduledCard';
import VisitSlotCard, { VisitSlotMetadata } from './VisitSlotCard';
import { VisitFollowupCard, VisitFollowupMetadata } from './VisitFollowupCard';
import { cn } from '../../lib/utils';
import type { Message, Conversation } from '../../services/messagingService';

interface MessageMetadata {
  type:
    | 'text'
    | 'assignment_request'
    | 'assignment_response'
    | 'visit_scheduled'
    | 'visit_slot_request'
    | 'visit_slot_proposal'
    | 'visit_slot_confirmed'
    | 'visit_followup'
    | 'visit_decision';
  contactRequestId?: string;
  action?: 'accepted' | 'rejected';
  clientName?: string;
  propertyTitle?: string;
  propertyAddress?: string;
  professionalMessage?: string;
  visitDate?: string;
  visitNotes?: string;
  visitDuration?: number;
  slotId?: string;
  proposedBy?: 'admin' | 'client' | 'professional';
  actions?: Array<{ label: string; value: string }>;
  decision?: string;
  visitOutcome?: string;
}

interface ChatWindowProps {
  conversation: Conversation | null;
  messages: Message[];
  currentUserId: string;
  onSendMessage: (content: string) => Promise<void>;
  onBack?: () => void;
  isLoading?: boolean;
  isLoadingMessages?: boolean;
  isMobile?: boolean;
}

export function ChatWindow({
  conversation,
  messages,
  currentUserId,
  onSendMessage,
  onBack,
  isLoading,
  isLoadingMessages,
  isMobile,
}: ChatWindowProps) {
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!inputValue.trim() || isSending) return;

    setIsSending(true);
    try {
      await onSendMessage(inputValue.trim());
      setInputValue('');
      textareaRef.current?.focus();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!conversation) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-50 text-center p-8">
        <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mb-4">
          <Send className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Sélectionnez une conversation
        </h3>
        <p className="text-sm text-gray-500 max-w-xs">
          Choisissez une conversation dans la liste pour commencer à discuter
        </p>
      </div>
    );
  }

  const { otherParticipant } = conversation;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white">
        {onBack && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="mr-1"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">
            {otherParticipant.firstName} {otherParticipant.lastName}
          </h3>
          <p className="text-xs text-gray-500">{otherParticipant.email}</p>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {isLoadingMessages ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF9F68]"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <p className="text-gray-500 text-sm mb-2">Aucun message</p>
            <p className="text-gray-400 text-xs">
              Envoyez un message pour démarrer la conversation
            </p>
          </div>
        ) : (
          <>
            {messages.map((message) => {
              const metadata = message.metadata as MessageMetadata | null;
              const isOwn = message.senderId === currentUserId;
              const senderName = !isOwn
                ? `${message.sender.firstName} ${message.sender.lastName}`
                : undefined;

              // Assignment request message (read-only preview)
              if (metadata?.type === 'assignment_request' && metadata.contactRequestId) {
                return (
                  <AssignmentMessageCard
                    key={message.id}
                    content={message.content}
                    metadata={metadata as AssignmentMessageMetadata}
                    timestamp={message.createdAt}
                    isOwn={isOwn}
                    senderName={senderName}
                  />
                );
              }

              // Check if this is an assignment response message
              if (metadata?.type === 'assignment_response') {
                return (
                  <AssignmentResponseCard
                    key={message.id}
                    content={message.content}
                    metadata={metadata as AssignmentResponseMetadata}
                    timestamp={message.createdAt}
                    isOwn={isOwn}
                    senderName={senderName}
                  />
                );
              }

              // Visit scheduled message
              if (metadata?.type === 'visit_scheduled') {
                return (
                  <VisitScheduledCard
                    key={message.id}
                    content={message.content}
                    metadata={metadata as VisitScheduledMetadata}
                    timestamp={message.createdAt}
                    isOwn={isOwn}
                    senderName={senderName}
                  />
                );
              }

              // Visit slot request/proposal/confirmed
              if (
                metadata?.type === 'visit_slot_request' ||
                metadata?.type === 'visit_slot_proposal' ||
                metadata?.type === 'visit_slot_confirmed'
              ) {
                return (
                  <VisitSlotCard
                    key={message.id}
                    content={message.content}
                    metadata={metadata as VisitSlotMetadata}
                    timestamp={message.createdAt}
                    isOwn={isOwn}
                    senderName={senderName}
                    role={conversation.otherParticipant.role?.toLowerCase() as any}
                  />
                );
              }

              // Visit followup message with action buttons
              if (metadata?.type === 'visit_followup' && metadata.contactRequestId) {
                return (
                  <VisitFollowupCard
                    key={message.id}
                    content={message.content}
                    metadata={metadata as VisitFollowupMetadata}
                    timestamp={message.createdAt}
                    isOwn={isOwn}
                    senderName={senderName}
                    onDecisionSubmitted={() => {
                      // Messages will be refreshed via socket events
                    }}
                  />
                );
              }

              // Visit decision confirmation
              if (metadata?.type === 'visit_decision') {
                const decisionLabels: Record<string, string> = {
                  WANT: 'Je veux le bien',
                  THINKING: 'Je réfléchis encore',
                  NOT_INTERESTED: 'Je ne veux pas le bien',
                };
                return (
                  <MessageBubble
                    key={message.id}
                    content={message.content}
                    timestamp={message.createdAt}
                    isOwn={isOwn}
                    senderName={senderName}
                    isRead={!!message.readAt}
                  />
                );
              }

              // Default to regular message bubble
              return (
                <MessageBubble
                  key={message.id}
                  content={message.content}
                  timestamp={message.createdAt}
                  isOwn={isOwn}
                  senderName={senderName}
                  isRead={!!message.readAt}
                />
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <div className="flex items-end gap-2">
          <Textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Écrivez votre message..."
            className="min-h-[44px] max-h-32 resize-none"
            rows={1}
            disabled={isSending}
          />
          <Button
            onClick={handleSend}
            disabled={!inputValue.trim() || isSending}
            className="bg-[#FF9F68] hover:bg-[#E67E4F] h-[44px] px-4"
          >
            {isSending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ChatWindow;

